/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSurveysStore } from '@/stores/surveys.store';
import { useSessionReplayStore } from '@/stores/session-replay.store';
import { useSavedReportsStore } from '@/stores/saved-reports.store';
import { useGuidesStore } from '@/stores/guides.store';
import { canViewSessionReplayForApp } from '@/vuex/modules/auth/auth.helpers';
import { saveInsightsSegment, triggerMLPipeline } from '@/insights/utils/utils';
import { visitorList } from '@/vuex/modules/segments/segment-eligible-visitor-list.aggregation';
import { validateDelayedAnalyticsWithSegmentFilters } from '@/segments/delayed-segment-csv.utils';
import { runAggregation } from '@/utils/aggregation';
import {
    isSupportedGuideFilter,
    segmentRuleNotSupportedText,
    getEnabledDefaultSegments,
    isEmailSegmentGuideEligible,
    getDefaultSegment,
    isAdoptSegment,
} from '@utils/segments.js';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import values from 'lodash/values';
import isString from 'lodash/isString';
import { findIdentified } from '@pendo/agg-connect';
import {
    fetchSegments,
    fetchSegment,
    createSegment as createSegmentApi,
    deleteSegment,
    updateSegment,
    filtersHaveRulesOfKind,
    sendSaveSegmentTrackEvents,
} from '@/vuex/modules/segments/segment.helpers';
import { getPipelineProps } from '@/segments/segment';
import { updateVerificationState } from '@/common/verification-status/verification-status.utils';
import { i18n_t as $t } from '@utils/i18n';
import { hasSubscriptionFlag, hasSegmentFlag } from '@/data/flags/local-flags';
import { hasNoAccountRules, isDefaultSegment } from '@/utils/segments';
import { getGuideTargetingSegment } from '@/guides/helpers/common.util';
import { defineStore, acceptHMRUpdate } from 'pinia';

let loadAllPromise: Promise<any> | null = null;

const editorDefaults = {
    confirmDelete: false,
    saved: false,
    segment: null as any,
    visitorCount: null as any,
};

const customSegmentDefaults = {
    id: 'custom',
    name: 'Custom Segment',
    definition: {
        filters: [] as any[],
        advancedOptions: {} as any,
    },
    pipeline: [] as any[],
    pipelines: undefined as any,
    compound: undefined as any,
};

const apiSegmentFields = { username: 'API', first: 'API', last: 'API' };

const activeSegmentDefaults = {
    segment: {} as any,
    truncatedVisitorList: [] as any[],
    eligibleVisitorCount: undefined as number | undefined,
    fetching: false,
};

function getSelectedVisitorType (pipeline: any[]) {
    return get(findIdentified(pipeline), 'identified', null);
}

function getInitialState () {
    return {
        map: {} as Record<string, any>,
        error: {} as Record<string, any>,
        editor: Object.assign({}, editorDefaults),
        customSegment: Object.assign({}, customSegmentDefaults),
        activeSegmentDetails: Object.assign({}, activeSegmentDefaults),
        fetching: false,
        isDeleteModalOpen: false,
        defaultSegments: [] as any[],
        segmentDependents: {} as Record<string, any>,
        segmentsToDelete: [] as any[],
        selectedSegments: [] as any[],
        isInsightsSegmentEligible: false,
    };
}

export const useSegmentsStore = defineStore('segments', {
    state: getInitialState,
    getters: {
        list (state): any[] {
            return sortBy(values(state.map), (segment: any) => get(segment, 'name', '').toLowerCase());
        },
        segmentById (): (id: string) => any {
            return (id: string) => this.customSegmentById(id) || this.defaultSegmentById(id);
        },
        defaultSegmentById (state): (id: string) => any {
            return (id: string) => state.defaultSegments.find((defaultSegment: any) => defaultSegment.id === id);
        },
        customSegmentById (state): (id: string) => any {
            return (id: string) => state.map[id];
        },
        areSegmentDependentsLoading (state): (segments: any[]) => boolean {
            return (segments: any[]) => {
                if (!segments || !segments.length) return false;

                return segments.some((segment: any) => {
                    const { id } = segment;
                    const segmentDependents = state.segmentDependents[id];

                    return !segmentDependents || segmentDependents.fetching;
                });
            };
        },
        countSegmentDependents (state): (segment: any) => number {
            return (segment: any) => {
                if (!segment) return -1;

                const dependents = state.segmentDependents[segment.id];

                if (!dependents || dependents.fetching) return -1;

                const dependentCount = Object.values(dependents).reduce((total: number, current: any) => {
                    return Array.isArray(current) ? total + current.length : total;
                }, 0);

                return dependentCount;
            };
        },
        selectedSegmentsMap (state): Record<string, number> {
            return state.selectedSegments.reduce((map: Record<string, number>, segment: any, index: number) => {
                map[segment.id] = index;

                return map;
            }, {});
        },
        parentSegmentsById (): (id: string) => any[] {
            return (id: string) => (id ? this.list.filter((segment: any) => segment.compound?.includes(id)) : []);
        },
        segmentIsGuideFriendly (): (segment: any) => boolean {
            return (segment: any) => this.getGuideUnfriendlyRulesForSegment(segment).length <= 0 && !!segment?.shared;
        },
        getGuideUnfriendlyRulesForSegment (): (segment: any, segmentLookup?: any, featureName?: string) => any[] {
            return (segment: any, segmentLookup?: any, featureName?: string) => {
                if (!isEmailSegmentGuideEligible(segment)) {
                    const filter = { type: 'email' };

                    return [
                        {
                            filter,
                            tooltip: segmentRuleNotSupportedText(filter, featureName),
                        },
                    ];
                }

                return (segment?.definition?.filters ?? [])
                    .reduce((acc: any[], curr: any) => (Array.isArray(curr.or) ? [...acc, ...curr.or] : [...acc, curr]), [])
                    .filter((filter: any) => {
                        if (filter.id === 'segment') {
                            const findSegment = segmentLookup || this.segmentById;

                            return !this.segmentIsGuideFriendly(findSegment(filter.segmentId));
                        }

                        return !isSupportedGuideFilter(filter);
                    })
                    .map((filter: any) => ({ filter, tooltip: segmentRuleNotSupportedText(filter, featureName) }));
            };
        },
        getSegmentGuideEligibility (): (segment: any, segmentLookup?: any, featureName?: string) => { eligible: boolean; reason?: string } {
            return (segment: any, segmentLookup?: any, featureName?: string) => {
                let eligible = true;
                let reason: string | undefined;
                if (!segment.shared) {
                    eligible = false;
                    reason = $t(
                        'segmentsModule.getSegmentGuideEligibility.reason.This segment is incompatible with {featureName}: visibility set to only me',
                        { featureName: featureName || 'guides' },
                    );
                } else {
                    const unfriendlyRules = this.getGuideUnfriendlyRulesForSegment(segment, segmentLookup, featureName);

                    if (unfriendlyRules.length > 0) {
                        eligible = false;
                        reason = unfriendlyRules[0].tooltip;
                    }
                }

                return { eligible, reason };
            };
        },
        everyPossibleRuleDependency (state): any {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when remaining modules migrate to Pinia
            const dependents: any = {};
            dependents.tags = [
                ...vuexStore.getters['pages/list'],
                ...vuexStore.getters['features/list'],
                ...vuexStore.getters['trackTypes/list'],
                ...useGuidesStore().list,
                ...useGuidesStore().archivedList,

                // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                ...require('@/stores/journeys.store').useJourneysStore().list, // TODO APP-157977: journeys migrated to Pinia
                ...vuexStore.getters['emails/list'],
                ...vuexStore.getters['experiments/getAllExperiments'],
                ...useSurveysStore().listItems,
            ];

            dependents.segments = [...this.list, ...state.defaultSegments];

            dependents.apps = vuexStore.getters['apps/list'];
            dependents.workflows = vuexStore.getters['workflows/list'];
            const types = ['auto', 'agent', 'custom', 'salesforce'];
            dependents.schema = [
                pick(vuexStore.state.metadata.map.visitor, types),
                pick(vuexStore.state.metadata.map.account, types),
            ];

            return dependents;
        },
        listAdoptUISegments (): any[] {
            return this.list.filter((segment: any) => isAdoptSegment(segment));
        },
    },
    actions: {
        async loadAll ({ reFetch = false, excludeBlacklist = false } = {}) {
            if (loadAllPromise && !reFetch) {
                await loadAllPromise;

                return;
            }

            this.fetching = true;

            loadAllPromise = Promise.all([
                fetchSegments(),
                getEnabledDefaultSegments(excludeBlacklist),
                // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
                require('@/vuex/store').default.dispatch('insights/loadAllFocusGroups', {}), // TODO: replace when insights migrates to Pinia
            ]);
            try {
                const [segments, defaultSegments, focusGroups = []] = await loadAllPromise;
                for (const segment of segments) {
                    if (segment.createdByApi) {
                        Object.assign(segment.lastUpdatedByUser, apiSegmentFields);
                        Object.assign(segment.createdByUser, apiSegmentFields);
                        Object.assign(segment.definition, {
                            apiManagedList: true,
                            filters: [
                                {
                                    group: 'auto',
                                    kind: 'visitor',
                                    field: 'visitor.auto.id',
                                    id: 'visitor_auto_id',
                                    type: 'visitor.auto.id',
                                    name: 'Visitor ID',
                                    schema: 'string',
                                    operator: 'listContains',
                                    $valid: true,
                                    analyticsDelayedAtTimestamp: null,
                                },
                            ],
                        });
                    }
                }

                const focusGroupsWithoutEveryone = focusGroups.filter((focusGroup: any) => focusGroup.id !== '0');

                this.map = keyBy([...segments, ...focusGroupsWithoutEveryone], 'id');
                this.defaultSegments = defaultSegments;
            } catch (error) {
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async loadOne ({ reFetch = false, id }: { reFetch?: boolean; id: string }) {
            if (!reFetch) {
                await loadAllPromise;
            }

            if (this.map[id] && !reFetch) {
                return;
            }

            this.fetching = true;

            try {
                const segment = await fetchSegment(id);
                if (segment.createdByApi) {
                    Object.assign(segment.lastUpdatedByUser, apiSegmentFields);
                    Object.assign(segment.createdByUser, apiSegmentFields);
                }
                this.fetching = false;
                this.map[segment.id] = segment;
            } catch (error: any) {
                this.fetching = false;
                if (error && error.message !== 'cancelled') {
                    this.error[id] = error;
                }
                throw error;
            }
        },
        async saveSegment ({ segment, insightsAppId = undefined, location = '' }: { segment: any; insightsAppId?: string; location?: string }) {
            if (this.editor.segment.id === 'custom') {
                this.customSegment.definition.filters = segment.definition.filters;
                this.customSegment.pipeline = segment.pipeline;

                let visitorTypeCheckPipeline = segment.pipeline;

                if (segment.compound) {
                    this.customSegment.pipelines = segment.pipelines;
                    this.customSegment.compound = segment.compound;

                    if(segment.pipelines?.inlineIdentifiedVisitorsOnly) {
                        visitorTypeCheckPipeline = segment.pipelines?.inlineIdentifiedVisitorsOnly;
                    } else if(segment.pipelines?.inlineAnonymousVisitorsOnly) {
                        visitorTypeCheckPipeline = segment.pipelines?.inlineAnonymousVisitorsOnly;
                    }
                }

                const selectedVisitorType = get(findIdentified(visitorTypeCheckPipeline), 'identified', null);

                this.customSegment.definition.advancedOptions = {
                    ...get(segment, 'definition.advancedOptions'),
                    selectedVisitorType,
                };
                Object.assign(this.editor, editorDefaults);

                return this.customSegment;
            }

            try {
                const action = segment.id ? 'update' : 'create';
                let savedSegment: any = {};
                if (segment.isFocusGroup && insightsAppId) {
                    savedSegment = await saveInsightsSegment({ appId: insightsAppId, segment });
                    if (this.isInsightsSegmentEligible) {
                        triggerMLPipeline({ appId: insightsAppId, insightsSegmentId: savedSegment.id });
                    }
                } else {
                    const saveFn = action === 'update' ? updateSegment : createSegmentApi;
                    savedSegment = await saveFn(segment);

                    this.map[savedSegment.id] = savedSegment;

                    const parentSegmentIds = this.parentSegmentsById(savedSegment.id).map((parent: any) => parent.id);
                    /* eslint-disable no-await-in-loop */
                    for (const parentSegmentId of parentSegmentIds) {
                        let parentSegment = this.map[parentSegmentId];
                        if (!parentSegment) {
                            await this.loadOne({ id: parentSegmentId, reFetch: true });
                            parentSegment = this.map[parentSegmentId];
                        }
                        try {
                            const updatedPipelineProps = await getPipelineProps(parentSegment);
                            const updatedParent = { ...parentSegment, ...updatedPipelineProps };
                            await this.saveSegment({
                                segment: updatedParent,
                                insightsAppId,
                                location,
                            });
                        } catch (err) {
                            console.error(
                                `Failed to recompile parent segment ${parentSegment.id} after saving nested segment:`,
                                err,
                            );
                        }
                    }
                    /* eslint-enable no-await-in-loop */

                    if (get(this.activeSegmentDetails, 'segment.id') === savedSegment.id) {
                        this.loadActiveSegment({
                            segment: savedSegment,
                            reFetchDependents: false,
                        });
                    }

                    sendSaveSegmentTrackEvents(savedSegment, { action, location });
                }
                Object.assign(this.editor, editorDefaults);

                return savedSegment;
            } catch (error) {

                console.error(error);
                this.error[segment.id] = error;
                throw error;
            }
        },
        async updateSegmentVerificationState ({ id, status, notes }: { id: string; status: any; notes: any }) {
            await updateVerificationState({
                kind: 'segment',
                id,
                state: status,
                notes,
            });

            await this.loadOne({ id, reFetch: true });

            if (get(this.activeSegmentDetails, 'segment.id') === id) {
                this.loadActiveSegment({
                    segment: this.map[id],
                    reFetchDependents: false,
                });
            }

            if (get(this.editor, 'segment.id') === id) {
                this.editor.segment = cloneDeep(this.map[id]);
            }
        },
        async simpleSave ({ segment }: { segment: any }) {
            try {
                const action = segment.id && segment.id !== 'draft0' ? 'update' : 'create';
                const saveFn = action === 'update' ? updateSegment : createSegmentApi;
                const savedSegment = await saveFn(segment);
                this.map[savedSegment.id] = savedSegment;

                sendSaveSegmentTrackEvents(savedSegment, 'create');

                return savedSegment;
            } catch (error) {

                console.error(error);
                this.error[segment.id] = error;
                throw error;
            }
        },
        setSegmentsToDelete (segments: any[]) {
            this.loadDependents({ segments, reFetch: false, reCheckDependencies: true });
            this.segmentsToDelete = cloneDeep(segments);
        },
        deleteSegment ({ segmentId }: { segmentId: string } = { segmentId: '' }) {
            const segment = this.map[segmentId];
            const selectedSegmentIndex = get(this.selectedSegmentsMap, segmentId, -1);
            if (selectedSegmentIndex > -1) {
                this.selectedSegments.splice(selectedSegmentIndex, 1);
            }

            if (segmentId === get(this.activeSegmentDetails.segment, 'id')) {
                Object.assign(this.activeSegmentDetails, activeSegmentDefaults);
            }

            delete this.map[segmentId];

            return deleteSegment(segment).catch((error: any) => {
                if (error && error.message !== 'cancelled') {
                    this.error[segment.id] = error;
                }
                throw error;
            });
        },
        async loadDependents (
            { segments, reFetch = true, reCheckDependencies = false }: { segments: any[]; reFetch?: boolean; reCheckDependencies?: boolean },
        ) {
            if (!segments.length) return Promise.resolve();
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when remaining modules migrate to Pinia
            const activeSub = vuexStore.state.subscription.active.id;
            const dependentsNotLoadedSegments: any[] = [];

            segments.forEach((segment: any) => {
                const { id } = segment;
                if (reFetch || !this.segmentDependents[id] || reCheckDependencies) {
                    this.segmentDependents[id] = Object.freeze({ fetching: true });
                    dependentsNotLoadedSegments.push(segment);
                }
            });

            if (!dependentsNotLoadedSegments.length) {
                return Promise.resolve();
            }

            const canUseReplay = vuexStore.getters['subscription/hasSessionReplayEnabled'];
            const canUseEmails = vuexStore.getters['emails/hasEmailStandAlone'];

            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const { useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const journeysStore = useJourneysStore();
            const canUseJourneys = journeysStore.hasJourneysEnabled;
            const canUseFeatureFlags = hasSegmentFlag('featureFlags');

            const loadPromises: Promise<any>[] = [];
            const modules = [
                { shouldFetch: canUseReplay, getter: 'apps/list', loadAction: 'apps/loadAll' },
                { shouldFetch: canUseEmails, getter: 'emails/list', loadAction: 'emails/loadAll' },
            ];
            for (const { shouldFetch, getter, loadAction } of modules) {
                if (shouldFetch && (reFetch || !vuexStore.getters[getter].length)) {
                    loadPromises.push(vuexStore.dispatch(loadAction, {}));
                }
            }
            const savedReportsStore = useSavedReportsStore();
            if (reFetch || !savedReportsStore.list.length) {
                loadPromises.push(savedReportsStore.loadAll());
            }
            if (reFetch || !useGuidesStore().list.length) {
                loadPromises.push(useGuidesStore().loadAll());
            }
            if (canUseJourneys && (reFetch || !journeysStore.list.length)) {
                loadPromises.push(journeysStore.loadAllJourneys());
            }
            if (canUseFeatureFlags && (reFetch || !vuexStore.getters['featureFlags/list'].length)) {
                loadPromises.push(vuexStore.dispatch('featureFlags/loadAll', {}));
            }
            if (loadPromises.length) {
                await Promise.all(loadPromises);
            }

            const guides = useGuidesStore().list;
            const reports = savedReportsStore.list;
            const emails = vuexStore.getters['emails/list'];
            const journeys = journeysStore.list;
            const featureFlags = canUseFeatureFlags ? vuexStore.getters['featureFlags/list'] : [];

            let sessionReplayAppSettings: any[] | undefined;
            if (canUseReplay) {
                const apps = vuexStore.getters['apps/list'];
                sessionReplayAppSettings = await Promise.all(
                    apps
                        .filter(({ id }: any) => vuexStore.getters['apps/getApplicationFlag'](id, 'enableSessionReplay') && canViewSessionReplayForApp(id))
                        .map(async (app: any) => {
                            let replaySettings;
                            try {
                                replaySettings = await useSessionReplayStore().loadReplaySettingsForApp({ appId: app.id });
                            } catch (err) {
                                console.error('Unable to fetch replay settings for app: ', err);
                            }

                            return {
                                replaySettings,
                                app,
                            };
                        }),
                );
            }

            dependentsNotLoadedSegments.forEach((segment: any) => {
                const { id } = segment;

                const dependentCustomSegments: any[] = [];
                const dependentGuides: any[] = [];
                const dependentEmails: any[] = [];
                const dependentJourneys: any[] = [];
                const dependentFeatureFlags: any[] = [];

                const mapGuideToDependent = (guide: any) => ({
                    ...pick(guide, 'id', 'name', 'state'),
                    routeObj: { name: 'GuideDetails', params: { guideId: guide.id, sub: activeSub } },
                });

                const mapEmailToDependent = (email: any) => ({
                    ...pick(email, 'id', 'name', 'status'),
                    routeObj: { name: 'EmailDetails', params: { id: email.id, sub: activeSub } },
                });

                const mapJourneyToDependent = (journey: any) => ({
                    ...pick(journey, 'id', 'name', 'status'),
                    routeObj: { name: 'JourneyDetails', params: { id: journey.id, sub: activeSub } },
                });

                const mapFeatureFlagToDependent = (flag: any) => ({
                    ...pick(flag, 'flagName', 'displayName'),
                    id: flag.flagName,
                    name: flag.displayName || flag.flagName,
                });

                if (canUseFeatureFlags) {
                    featureFlags.forEach((flag: any) => {
                        if (flag.segmentId === id) {
                            dependentFeatureFlags.push(mapFeatureFlagToDependent(flag));
                        }
                    });
                }

                guides.forEach((guide: any) => {
                    const filters = guide.audienceUiHint?.filters;
                    const segmentInFilters = filters?.some((filter: any) =>
                        (filter.or ? filter.or.some((rule: any) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push(mapGuideToDependent(guide));
                    }

                    return dependentGuides.push(mapGuideToDependent(guide));
                });

                emails.forEach((email: any) => {
                    const { attributes, audienceUIHint = {} } = email;
                    const { filters } = audienceUIHint;

                    if (attributes?.journeyId) return;

                    const segmentInFilters = filters?.some((filter: any) =>
                        (filter.or ? filter.or.some((rule: any) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push({ ...mapEmailToDependent(email), type: 'email' });
                    }

                    return dependentEmails.push(mapEmailToDependent(email));
                });

                journeys.forEach((journey: any) => {
                    const { audienceUiHint = {} } = journey;
                    const { filters } = audienceUiHint;

                    const segmentInFilters = filters?.some((filter: any) =>
                        (filter.or ? filter.or.some((rule: any) => rule.segmentId === id) : filter.segmentId === id),
                    );

                    if (!segmentInFilters) return;

                    const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                    if (isCustomNestedSegment) {
                        return dependentCustomSegments.push({ ...mapJourneyToDependent(journey), type: 'journey' });
                    }

                    return dependentJourneys.push(mapJourneyToDependent(journey));
                });

                const dependentDraftSegments: any[] = [];
                const dependentFullReports: any[] = [];

                reports.forEach((report: any) => {
                    if (report.segmentId === id) {
                        dependentFullReports.push(report);
                    }

                    if (report.segments) {
                        const { segments } = report;

                        segments.forEach((seg: any) => {
                            if (seg.compound?.includes(id)) {
                                const miniReport = pick(report, 'id', 'name');
                                dependentDraftSegments.push({ report: miniReport, draftSegment: seg });
                            }
                        });

                        if (segments.some((seg: any) => seg.id === id)) dependentFullReports.push(report);
                    }

                    if (report.segments?.includes((seg: any) => seg.id === id)) dependentFullReports.push(report);
                });

                const dependentReports = dependentFullReports.map((report: any) => {
                    const miniReport = pick(report, 'id', 'name');
                    let namedRoute: string;
                    if (report.definition?.type === 'List') {
                        switch (report.definition.kind.toLowerCase()) {
                            case 'account':
                                namedRoute = 'AccountListReport';
                                break;
                            case 'parentaccount':
                                namedRoute = 'ParentAccountListReport';
                                break;
                            case 'visitor':
                                namedRoute = 'VisitorListReport';
                                break;
                            default:
                                namedRoute = 'NotFound';
                                break;
                        }
                        (miniReport as any).routeObj = {
                            name: namedRoute,
                            params: { reportId: report.id, sub: activeSub },
                        };
                    } else if (report.type === 'Saved Reports') {
                        (miniReport as any).routeObj = {
                            name: 'DataExplorer',
                            params: { id: report.id, sub: activeSub },
                        };
                    } else {
                        switch (report.type?.toLowerCase()) {
                            case 'funnel':
                                namedRoute = 'FunnelDetails';
                                break;
                            case 'path':
                                namedRoute = 'PathDetails';
                                break;
                            case 'retention':
                                namedRoute = 'RetentionDetails';
                                break;
                            default:
                                namedRoute = 'NotFound';
                                break;
                        }
                        (miniReport as any).routeObj = {
                            name: namedRoute,
                            params: { reportId: report.id, sub: activeSub },
                        };
                    }

                    return miniReport;
                });
                const dependentSegments = this.parentSegmentsById(id);

                const dependentReplayApps: any[] = [];

                if (canUseReplay) {
                    const mapAppToDependent = (app: any) => ({
                        name: app.displayName,
                        id: app.id,
                        routeObj: {
                            name: 'AppAdmin',
                            params: { appId: app.id, sub: activeSub },
                            query: { tab: 'replay' },
                        },
                    });

                    sessionReplayAppSettings!.forEach(({ replaySettings, app }: any) => {
                        const filters = get(replaySettings, 'audienceUiHint.filters', []);

                        if (!filters.length) return;

                        const segmentInFilters = filters.some((filter: any) =>
                            (filter.or ? filter.or.some((rule: any) => rule.segmentId === id) : filter.segmentId === id),
                        );

                        if (!segmentInFilters) return;

                        const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');

                        if (isCustomNestedSegment) {
                            return dependentCustomSegments.push({ ...mapAppToDependent(app), type: 'replay' });
                        }

                        return dependentReplayApps.push(mapAppToDependent(app));
                    });
                }

                this.segmentDependents[id] = Object.freeze({
                    dependentGuides,
                    dependentEmails,
                    dependentJourneys,
                    dependentFeatureFlags,
                    dependentReports,
                    dependentSegments,
                    dependentDraftSegments,
                    dependentCustomSegments,
                    dependentReplayApps,
                    fetching: false,
                });
            });
        },
        cancelEditSegment () {
            Object.assign(this.editor, editorDefaults);
        },
        setSelectedSegments (segments: any[]) {
            this.selectedSegments = cloneDeep(segments);
        },
        clearSegmentDependents () {
            this.segmentDependents = {};
        },
        setEditorWithSegmentId ({ segmentId }: { segmentId: string }) {
            this.editor.segment = cloneDeep(this.map[segmentId]);
        },
        setIsInsightsSegmentEligible (isEligible: boolean) {
            this.isInsightsSegmentEligible = isEligible;
        },
        clearActiveSegmentDetails () {
            Object.assign(this.activeSegmentDetails, activeSegmentDefaults);
        },
        updateCustomSegment ({ filters, pipeline, pipelines, compound, advancedOptions = {} }: { filters: any[]; pipeline: any[]; pipelines?: any; compound?: any; advancedOptions?: any }) {
            this.customSegment.definition.filters = filters;
            this.customSegment.pipeline = pipeline;

            if (compound) {
                this.customSegment.pipelines = pipelines;
                this.customSegment.compound = compound;
            }

            const selectedVisitorType = getSelectedVisitorType(pipeline);

            this.customSegment.definition.advancedOptions = {
                ...advancedOptions,
                selectedVisitorType,
            };
        },
        clearCustomSegment () {
            this.updateCustomSegment({ pipeline: [], filters: [] });
        },
        createSegment () {
            this.cloneSegment({ createNew: true });
        },
        createInsightsSegment () {
            this.cloneSegment({ createNew: true, isInsightsSegment: true });
        },
        createSegmentWithDefinition (
            { definition = {} as any, name = 'Unnamed', shared = true, description = '' }: { definition?: any; name?: string; shared?: boolean; description?: string },
        ) {
            if (!definition.filters) {
                definition.filters = [];
            }
            if (!definition.advancedOptions) {
                definition.advancedOptions = { selectedVisitorType: null };
            }
            const newSegment = {
                definition,
                name,
                shared,
                description,
                flagName: '',
                kind: 'Segment',
            };

            this.editor.segment = cloneDeep(newSegment);
        },
        async cloneSegment (
            { segmentId = null, createNew = false, readOnly = false, isInsightsSegment = false }: { segmentId?: string | null; createNew?: boolean; readOnly?: boolean; isInsightsSegment?: boolean } = {},
        ) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when auth migrates to Pinia
            let segmentToClone: any;
            if (createNew) {
                segmentToClone = {
                    definition: {
                        filters: [],
                        advancedOptions: {
                            selectedVisitorType: null,
                        },
                    },
                    ...(isInsightsSegment && { isFocusGroup: true }),
                    kind: 'Segment',
                    shared: !vuexStore.getters['auth/isReadOnly'],
                    description: '',
                };
            } else {
                await this.loadOne({ id: segmentId!, reFetch: true });
                segmentToClone = this.map[segmentId!];
            }
            const newSegment: any = cloneDeep(
                pick(segmentToClone, 'definition', 'shared', 'description', 'kind', 'isFocusGroup'),
            );
            newSegment.name = createNew ? '' : `Copy of ${segmentToClone.name}`;
            if (readOnly) {
                newSegment.shared = false;
            }
            newSegment.flagName = '';
            this.editor.segment = cloneDeep(newSegment);
        },
        async editSegment ({ segmentId }: { segmentId: string }) {
            await this.loadOne({ id: segmentId, reFetch: true });
            this.editor.segment = cloneDeep(this.map[segmentId]);
        },
        editCustomSegment () {
            this.editor.segment = cloneDeep(this.customSegment);
        },
        async loadActiveSegment ({ segment, reFetchAnalytics = true }: { segment: any; reFetchAnalytics?: boolean; reFetchDependents?: boolean }) {
            if (!segment) return;
            this.error[segment.id] = undefined;

            if (reFetchAnalytics) {
                Object.assign(this.activeSegmentDetails, activeSegmentDefaults);
                this.activeSegmentDetails.fetching = true;
            }

            const activeSegment: any = pick(
                segment,
                'name',
                'id',
                'definition',
                'pipeline',
                'pipelines',
                'compound',
                'createdByUser',
                'lastUpdatedByUser',
                'shared',
                'description',
                'createdByApi',
                'verification',
                'analyticsDelayed',
                'includeForOEM',
            );

            activeSegment.createdBy = segment.createdByUser.username;
            if (reFetchAnalytics) {
                activeSegment.analyticsDelayed = await validateDelayedAnalyticsWithSegmentFilters(
                    activeSegment?.definition?.filters,
                );
            }

            this.activeSegmentDetails.segment = activeSegment;

            try {
                if (reFetchAnalytics) {
                    await this.loadVisitorListForSegmentId({
                        segmentId: segment.id,
                    });
                }
            } catch (error: any) {
                this.activeSegmentDetails.fetching = false;
                if (error && error.message !== 'cancelled') {
                    this.error[segment.id] = { message: error.data };
                }
            }

            this.activeSegmentDetails.fetching = false;
        },
        loadVisitorListForSegmentId ({ segmentId }: { segmentId: string }) {
            if (!segmentId) return;

            return runAggregation(visitorList({ segment: this.map[segmentId] })).then((result: any) => {
                const { eligibleVisitorCount, visitorList: vList } = result[0];
                this.activeSegmentDetails.truncatedVisitorList = vList;
                this.activeSegmentDetails.eligibleVisitorCount = eligibleVisitorCount;
            });
        },
        loadEveryPossibleRuleDependency () {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when remaining modules migrate to Pinia
            const canUseEmails = vuexStore.getters['emails/hasEmailStandAlone'];

            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const { useJourneysStore: _useJourneysStore } = require('@/stores/journeys.store'); // TODO APP-157977: journeys migrated to Pinia
            const _journeysStore = _useJourneysStore();
            const canUseJourneys = _journeysStore.hasJourneysEnabled;

            const dependencies: Promise<any>[] = [
                this.loadAll(),
                vuexStore.dispatch('apps/loadAll', {}),
                vuexStore.dispatch('metadata/loadAll', {}),
                vuexStore.dispatch('pages/loadAll', {}),
                vuexStore.dispatch('features/loadAll', {}),
                useGuidesStore().loadAll(),
                vuexStore.dispatch('trackTypes/loadAll', {}),
                vuexStore.dispatch('workflows/loadAll', {}),
                vuexStore.dispatch('experiments/fetchAllExperiments', {}),
                useSurveysStore().loadAllItems(),
            ];

            if (canUseEmails) {
                dependencies.push(vuexStore.dispatch('emails/loadAll', {}));
            }

            if (canUseJourneys) {
                dependencies.push(_journeysStore.loadAllJourneys());
            }

            return Promise.all(dependencies);
        },
        processReplaySegment ({ audience, audienceUiHint }: { audience?: any; audienceUiHint?: any } = {}) {
            let audienceSegment: any;
            if (!audience || !audienceUiHint) {
                audienceSegment = getDefaultSegment('everyone');

                return audienceSegment;
            }

            const filters = get(audienceUiHint, 'filters', []);
            const ruleCount = filters.length;
            const isCustomNestedSegment = filtersHaveRulesOfKind(filters, 'segment');
            const segmentId = get(filters, '[0].segmentId');
            const selectedVisitorType = get(findIdentified(audience), 'identified', null);
            let isFilteringByVisitorType: boolean | undefined;
            const captureReplayForAnon = hasSubscriptionFlag('showAnonymous');

            if (captureReplayForAnon) {
                isFilteringByVisitorType = selectedVisitorType !== null;
            }

            const isExistingSegment = !isCustomNestedSegment && segmentId;
            const isCustomSegment = ruleCount || isFilteringByVisitorType;

            this.updateCustomSegment({ filters: [], pipeline: [] });

            const segments = this.list;
            const { defaultSegments, customSegment } = this;

            if (isExistingSegment) {
                audienceSegment = segments.concat(defaultSegments).find((segment: any) => segment.id === segmentId);
                if (!audienceSegment) {
                    audienceSegment = {
                        id: segmentId,
                        name: 'Unknown Segment',
                    };
                }
            }
            if (!isExistingSegment && isCustomSegment) {
                const rebuildCustomSegment = { filters, pipeline: audience };
                this.updateCustomSegment(rebuildCustomSegment);
                audienceSegment = cloneDeep(customSegment);
            }
            if (!isExistingSegment && !isCustomSegment) {
                audienceSegment = getDefaultSegment('everyone');
            }

            return audienceSegment;
        },
        async hasGlobalSegmentWithNoAccountRules ({ globalSegment }: { globalSegment: any }) {
            if (!globalSegment) {
                return true;
            }

            const id = globalSegment;
            if (id === null || isDefaultSegment(id) || id === 'custom') {
                return true;
            }

            await this.loadOne({ id });
            const segment = this.map[id];
            const filters = get(segment, ['definition', 'filters'], []);

            return hasNoAccountRules(filters);
        },
        async getGuideSegmentAggProps ({ guideTargetingSegment, guide }: { guideTargetingSegment: any; guide: any }) {
            if (guideTargetingSegment && isString(guideTargetingSegment) && !isDefaultSegment(guideTargetingSegment)) {
                await this.loadOne({ id: guideTargetingSegment });
                const segment = this.map[guideTargetingSegment];
                const filters = get(segment, ['definition', 'filters'], []);

                return {
                    guideSegmentIdentified: get(findIdentified(segment.pipeline), 'identified', null),
                    hasGuideSegmentWithNoAccountRules: hasNoAccountRules(filters),
                    isNestedSegment: filtersHaveRulesOfKind(filters, 'segment'),
                };
            }

            const filters = get(guide, ['audienceUiHint', 'filters'], []);

            return {
                guideSegmentIdentified: null,
                hasGuideSegmentWithNoAccountRules: hasNoAccountRules(filters),
            };
        },
        async getGuideSegmentVisitorCountAggParams ({ guide, segmentId = false }: { guide: any; segmentId?: any }) {
            // eslint-disable-next-line @typescript-eslint/no-require-imports, no-warning-comments
            const vuexStore = require('@/vuex/store').default; // TODO: replace when apps migrates to Pinia
            const guideTargetingSegment = getGuideTargetingSegment(guide);
            const guideAudienceIdentified = get(findIdentified(guide.audience), 'identified', null);
            const [guideSegmentAggProps, globalSegmentHasNoAccountRules] = await Promise.all([
                this.getGuideSegmentAggProps({ guideTargetingSegment, guide }),
                this.hasGlobalSegmentWithNoAccountRules({ globalSegment: segmentId }),
            ]);
            const { guideSegmentIdentified, hasGuideSegmentWithNoAccountRules, isNestedSegment } = guideSegmentAggProps;
            const segmentsNeedAccountIds = !(hasGuideSegmentWithNoAccountRules && globalSegmentHasNoAccountRules);

            return {
                guideAudienceIdentified,
                guideSegmentIdentified,
                guideTargetingSegment,
                segmentsNeedAccountIds,
                isMultiApp: vuexStore.getters['apps/isMultiApp'],
                useGlobalSegment: !!segmentId,
                isNestedSegment,
            };
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSegmentsStore, module.hot));
}

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
